import { useCallback, useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import {
    ConsolidationRelationshipResponse,
    CreateConsolidationRelationshipBody,
    UpdateConsolidationRelationshipBody,
} from "@common/schemas/domains/accounting/consolidation.schema";
import {
    ConsolidationEntityRelationshipType,
    ConsolidationRelationshipMetadata,
} from "@common/types/domains/accounting/consolidation";
import {
    FULL_OWNERSHIP_PERCENTAGE,
    getOwnershipEntityDisplayName,
    RELATIONSHIP_PARTY_LABELS,
} from "../ownershipGraph.helpers";

interface MetadataFieldValues {
    name: string;
    ownershipType: string;
    payment: string;
    targetBalance: string;
    frequency: string;
    notes: string;
}

/**
 * Merges the form-managed fields into the existing metadata so fields the
 * form doesn't expose (e.g. roleLabel) survive an edit. Blank form fields
 * are removed; returns undefined when nothing remains. Exported for tests.
 */
export function buildMetadata(
    values: MetadataFieldValues,
    existing: ConsolidationRelationshipMetadata | null | undefined,
): ConsolidationRelationshipMetadata | undefined {
    const merged: ConsolidationRelationshipMetadata = { ...(existing ?? {}) };

    const name = values.name.trim();
    if (name) {
        merged.name = name;
    } else {
        delete merged.name;
    }
    const ownershipType = values.ownershipType.trim();
    if (ownershipType) {
        merged.ownershipType = ownershipType;
    } else {
        delete merged.ownershipType;
    }
    const payment = values.payment.trim();
    if (payment) {
        merged.payment = payment;
    } else {
        delete merged.payment;
    }
    const targetBalance = values.targetBalance.trim();
    if (targetBalance) {
        merged.targetBalance = targetBalance;
    } else {
        delete merged.targetBalance;
    }
    const frequency = values.frequency.trim();
    if (frequency) {
        merged.frequency = frequency;
    } else {
        delete merged.frequency;
    }
    const notes = values.notes.trim();
    if (notes) {
        merged.notes = notes;
    } else {
        delete merged.notes;
    }

    return Object.keys(merged).length > 0 ? merged : undefined;
}

interface UseRelationshipFormParams {
    isEdit: boolean;
    existing: ConsolidationRelationshipResponse | undefined;
    entities: Entity[];
    onCreate: (body: CreateConsolidationRelationshipBody) => void;
    onUpdate: (id: string, body: UpdateConsolidationRelationshipBody) => void;
    presetSourceEntityUuid?: string;
    presetTargetEntityUuid?: string;
}

function getInitialPercentage({
    existing,
    isEdit,
}: {
    existing: ConsolidationRelationshipResponse | undefined;
    isEdit: boolean;
}): string {
    if (
        existing?.ownershipPercentage !== null &&
        existing?.ownershipPercentage !== undefined
    ) {
        return String(existing.ownershipPercentage);
    }
    return isEdit ? "" : String(FULL_OWNERSHIP_PERCENTAGE);
}

/**
 * Form state, validation (mirroring the backend zod rules incl. the
 * multipleOf(0.01) percentage precision), and submit payload building for
 * the relationship create/edit pane.
 */
export function useRelationshipForm({
    isEdit,
    existing,
    entities,
    onCreate,
    onUpdate,
    presetSourceEntityUuid,
    presetTargetEntityUuid,
}: UseRelationshipFormParams) {
    const [type, setType] = useState<ConsolidationEntityRelationshipType>(
        existing?.type ?? ConsolidationEntityRelationshipType.ownership,
    );
    const [sourceEntityUuid, setSourceEntityUuid] = useState<string | null>(
        existing?.sourceEntityUuid ?? presetSourceEntityUuid ?? null,
    );
    const [targetEntityUuid, setTargetEntityUuid] = useState<string | null>(
        existing?.targetEntityUuid ?? presetTargetEntityUuid ?? null,
    );
    const [percentage, setPercentage] = useState(
        getInitialPercentage({ existing, isEdit }),
    );
    const [name, setName] = useState(existing?.metadata?.name ?? "");
    const [ownershipType, setOwnershipType] = useState(
        existing?.metadata?.ownershipType ?? "",
    );
    const [payment, setPayment] = useState(existing?.metadata?.payment ?? "");
    const [targetBalance, setTargetBalance] = useState(
        existing?.metadata?.targetBalance ?? "",
    );
    const [frequency, setFrequency] = useState(
        existing?.metadata?.frequency ?? "",
    );
    const [notes, setNotes] = useState(existing?.metadata?.notes ?? "");

    const partyLabels = RELATIONSHIP_PARTY_LABELS[type];
    const isOwnership = type === ConsolidationEntityRelationshipType.ownership;
    const isCashSweep = type === ConsolidationEntityRelationshipType.cashSweep;
    const hasPaymentField =
        type === ConsolidationEntityRelationshipType.loan ||
        type === ConsolidationEntityRelationshipType.rent ||
        type === ConsolidationEntityRelationshipType.managementFee;

    const businessEntities = useMemo(
        () => entities.filter(({ isBusiness }) => isBusiness),
        [entities],
    );
    const entityName = (uuid: string | null) => {
        const entity = entities.find(
            ({ uuid: entityUuid }) => entityUuid === uuid,
        );
        return entity ? getOwnershipEntityDisplayName(entity) : null;
    };

    const parsedPercentage = percentage.trim() ? Number(percentage) : undefined;
    const isProvidedPercentageValid =
        parsedPercentage !== undefined &&
        Number.isFinite(parsedPercentage) &&
        parsedPercentage > 0 &&
        parsedPercentage <= 100 &&
        // mirror the backend's multipleOf(0.01) precision rule
        Math.round(parsedPercentage * 100) / 100 === parsedPercentage;
    // In edit mode an empty percentage means "leave unchanged", so
    // metadata-only edits aren't blocked.
    const isPercentageValid =
        !isOwnership ||
        (isEdit && parsedPercentage === undefined) ||
        isProvidedPercentageValid;
    const canSubmit = isEdit
        ? isPercentageValid
        : Boolean(sourceEntityUuid && targetEntityUuid) &&
          sourceEntityUuid !== targetEntityUuid &&
          isPercentageValid;

    // Memoized so consumers (RelationshipFormPane publishes it to the page via
    // an effect) get a stable identity — an unstable one re-fires that effect
    // and loops the ownership page into "Maximum update depth exceeded".
    const handleSubmit = useCallback(() => {
        const metadata = buildMetadata(
            {
                name,
                ownershipType,
                payment,
                targetBalance,
                frequency,
                notes,
            },
            existing?.metadata,
        );
        if (isEdit && existing) {
            onUpdate(existing.id, {
                ...(isOwnership && parsedPercentage !== undefined
                    ? { ownershipPercentage: parsedPercentage }
                    : {}),
                metadata: metadata ?? null,
            });
            return;
        }
        if (!sourceEntityUuid || !targetEntityUuid) {
            return;
        }
        onCreate({
            type,
            sourceEntityUuid,
            targetEntityUuid,
            ...(isOwnership && parsedPercentage !== undefined
                ? { ownershipPercentage: parsedPercentage }
                : {}),
            ...(metadata ? { metadata } : {}),
        });
    }, [
        name,
        ownershipType,
        payment,
        targetBalance,
        frequency,
        notes,
        existing,
        isEdit,
        isOwnership,
        parsedPercentage,
        sourceEntityUuid,
        targetEntityUuid,
        type,
        onCreate,
        onUpdate,
    ]);

    return {
        type,
        setType,
        sourceEntityUuid,
        setSourceEntityUuid,
        targetEntityUuid,
        setTargetEntityUuid,
        percentage,
        setPercentage,
        name,
        setName,
        ownershipType,
        setOwnershipType,
        payment,
        setPayment,
        targetBalance,
        setTargetBalance,
        frequency,
        setFrequency,
        notes,
        setNotes,
        partyLabels,
        isOwnership,
        isCashSweep,
        hasPaymentField,
        businessEntities,
        entityName,
        canSubmit,
        handleSubmit,
    };
}
